import { z } from 'zod';

/**
 * Dashboard schemas — Stories 6.1 KPIs + 6.2 Inadimplentes + 6.3 Charges today +
 * 6.4 Revenue per product + 6.5 Revenue monthly + 6.6 Alerts + 6.7 snapshots +
 * 6.8 Divergence widget.
 * Zod parse em todas as respostas (Tier 2 mandatório).
 */

export const dashboardPeriodSchema = z.enum(['current_month', 'last_month', 'last_3m', 'last_12m']);
export type DashboardPeriod = z.infer<typeof dashboardPeriodSchema>;

const kpiCardSchema = z.object({
  value: z.number(),
  delta_pct: z.number().nullable(),
  trend: z.enum(['up', 'down', 'flat', 'unknown']),
});
export type KpiCard = z.infer<typeof kpiCardSchema>;

export const kpisResponseSchema = z.object({
  data: z.object({
    period: dashboardPeriodSchema,
    mrr: kpiCardSchema,
    revenue_received: kpiCardSchema,
    revenue_pending: kpiCardSchema,
    revenue_overdue: kpiCardSchema,
    overdue_count: z.number().int().nonnegative(),
    active_clients: z.number().int().nonnegative(),
    inadimplent_clients: z.number().int().nonnegative(),
    snapshot_date: z.string().nullable(),
  }),
});
export type KpisResponse = z.infer<typeof kpisResponseSchema>;
export type KpisData = KpisResponse['data'];

const alertSchema = z.object({
  id: z.string(),
  type: z.enum([
    'webhook_dlq',
    'webhook_silent',
    'divergence_unresolved',
    'charges_failed',
    'email_invalid',
    'provisioning_pending',
  ]),
  severity: z.enum(['warn', 'error']),
  message: z.string(),
  count: z.number().int().nonnegative(),
  link: z.string(),
});
export type DashboardAlert = z.infer<typeof alertSchema>;

export const alertsResponseSchema = z.object({
  data: z.object({
    alerts: z.array(alertSchema),
  }),
});
export type AlertsResponse = z.infer<typeof alertsResponseSchema>;

const inadimplenteRowSchema = z.object({
  client_id: z.string().uuid(),
  client_name: z.string(),
  client_type: z.enum(['pessoa_fisica', 'pessoa_juridica']),
  total_overdue: z.number().nonnegative(),
  charges_count: z.number().int().nonnegative(),
  oldest_due_date: z.string(),
  oldest_charge_id: z.string().uuid(),
  days_overdue: z.number().int().nonnegative(),
  email_invalid: z.boolean(),
  whatsapp: z.string().nullable(),
});
export type InadimplenteRow = z.infer<typeof inadimplenteRowSchema>;

export const inadimplentesResponseSchema = z.object({
  data: z.object({
    items: z.array(inadimplenteRowSchema),
  }),
});
export type InadimplentesResponse = z.infer<typeof inadimplentesResponseSchema>;

export const resendReminderResponseSchema = z.object({
  data: z.object({
    sent: z.boolean(),
    channel: z.string().nullable(),
    template_key: z.string(),
  }),
});
export type ResendReminderResponse = z.infer<typeof resendReminderResponseSchema>;

const chargeTodayRowSchema = z.object({
  id: z.string().uuid(),
  client_id: z.string().uuid(),
  client_name: z.string(),
  amount: z.number().nonnegative(),
  due_date: z.string(),
  status: z.string(),
  asaas_payment_id: z.string().nullable(),
  payment_link: z.string().nullable(),
  created_at: z.string(),
});
export type ChargeTodayRow = z.infer<typeof chargeTodayRowSchema>;

export const chargesTodayResponseSchema = z.object({
  data: z.object({
    items: z.array(chargeTodayRowSchema),
    total: z.number().int().nonnegative(),
  }),
});
export type ChargesTodayResponse = z.infer<typeof chargesTodayResponseSchema>;

const revenueByProductRowSchema = z.object({
  product_slug: z.string(),
  product_name: z.string(),
  mrr: z.number().nonnegative(),
  pct: z.number(),
});
export type RevenueByProductRow = z.infer<typeof revenueByProductRowSchema>;

export const revenueByProductResponseSchema = z.object({
  data: z.object({
    items: z.array(revenueByProductRowSchema),
  }),
});
export type RevenueByProductResponse = z.infer<typeof revenueByProductResponseSchema>;

const revenueMonthlyPointSchema = z.object({
  month: z.string(),
  mrr: z.number().nonnegative(),
  revenue_received: z.number().nonnegative(),
});
export type RevenueMonthlyPoint = z.infer<typeof revenueMonthlyPointSchema>;

export const revenueMonthlyResponseSchema = z.object({
  data: z.object({
    points: z.array(revenueMonthlyPointSchema),
  }),
});
export type RevenueMonthlyResponse = z.infer<typeof revenueMonthlyResponseSchema>;

const divergenceAlertRowSchema = z.object({
  id: z.string().uuid(),
  client_id: z.string().uuid().nullable(),
  client_name: z.string().nullable(),
  type: z.string(),
  detected_at: z.string(),
  resolved_at: z.string().nullable(),
  notes: z.string().nullable(),
  metadata: z.unknown(),
});
export type DivergenceAlertRow = z.infer<typeof divergenceAlertRowSchema>;

export const divergenceAlertsResponseSchema = z.object({
  data: z.object({
    items: z.array(divergenceAlertRowSchema),
    total: z.number().int().nonnegative(),
    page: z.number().int().positive().optional(),
    pageSize: z.number().int().positive().optional(),
  }),
});
export type DivergenceAlertsResponse = z.infer<typeof divergenceAlertsResponseSchema>;

export const divergenceAlertResolveResponseSchema = z.object({
  data: divergenceAlertRowSchema,
});
export type DivergenceAlertResolveResponse = z.infer<typeof divergenceAlertResolveResponseSchema>;

export const divergenceAlertTypesResponseSchema = z.object({
  data: z.array(
    z.object({
      type: z.string(),
      pending: z.number().int().nonnegative(),
      resolved: z.number().int().nonnegative(),
    }),
  ),
});
export type DivergenceAlertTypesResponse = z.infer<typeof divergenceAlertTypesResponseSchema>;

export const divergenceAlertResolveBulkResponseSchema = z.object({
  data: z.object({ resolved: z.number().int().nonnegative() }),
});
export type DivergenceAlertResolveBulkResponse = z.infer<
  typeof divergenceAlertResolveBulkResponseSchema
>;

// Story 5.11 — triagem de WhatsApp desconhecido

export const ignoredNumberRowSchema = z.object({
  id: z.string().uuid(),
  number: z.string(),
  notes: z.string().nullable(),
  created_at: z.string(),
});
export type IgnoredNumberRow = z.infer<typeof ignoredNumberRowSchema>;

export const ignoredNumbersResponseSchema = z.object({
  data: z.array(ignoredNumberRowSchema),
});
export type IgnoredNumbersResponse = z.infer<typeof ignoredNumbersResponseSchema>;

export const ignoreNumberResponseSchema = z.object({
  data: z.object({
    ignored: z.literal(true),
    resolvedCount: z.number().int().nonnegative(),
  }),
});
export type IgnoreNumberResponse = z.infer<typeof ignoreNumberResponseSchema>;

export const removeIgnoredNumberResponseSchema = z.object({
  data: z.object({ removed: z.literal(true) }),
});
export type RemoveIgnoredNumberResponse = z.infer<typeof removeIgnoredNumberResponseSchema>;

export const linkClientTargetSchema = z.enum(['whatsapp', 'financial_whatsapp', 'contact']);
export type LinkClientTarget = z.infer<typeof linkClientTargetSchema>;

export const linkClientResponseSchema = z.object({
  data: z.object({
    clientId: z.string().uuid(),
    communicationId: z.string().uuid(),
    resolvedCount: z.number().int().nonnegative(),
  }),
});
export type LinkClientResponse = z.infer<typeof linkClientResponseSchema>;
